/**
 * Price formatter
 */

/* eslint-disable simple-import-sort/imports */
import config, { Country, Language, Locale } from 'config';
import IntlPolyfill from 'intl';

import 'intl/locale-data/jsonp/en-ZA';
import 'intl/locale-data/jsonp/et-EE';
import 'intl/locale-data/jsonp/en-KE';

import { FormatPriceFn } from '../hooks/usePriceFormatter';

export type PriceFormatConfig = {
  language: Language;
  country?: Country;
  currency?: string;
  minAmountOfCents?: number;
  type?: 'currency' | 'decimal';
  disableCurrency?: boolean;
  [rest: string]: any;
};

export const defaultPriceFormatConfig = {
  minAmountOfCents: 0,
  type: 'currency',
};

/**
 * Some browsers do not have formatToParts(), so fix it
 * @param locales
 * @param options
 */
function getNumberFormatter(locale: Locale, options?: Intl.NumberFormatOptions) {
  const exists = typeof Intl?.NumberFormat?.prototype?.formatToParts === 'function';

  return exists ? new Intl.NumberFormat(locale, options) : new IntlPolyfill.NumberFormat(locale, options);
}

/**
 * Formats price based on locale
 * @param price
 * @param minAmountOfCents
 * @param style
 */
export default function formatPrice(
  price: string | number,
  {
    minAmountOfCents = 0, //
    type = 'currency',
    disableCurrency,
    language,
    country: overrideCountry,
    currency: overrideCurrency,
    ...rest
  }: PriceFormatConfig,
) {
  if (!language) {
    throw new Error('Language not provided');
  }

  const currency = overrideCurrency ?? config.get('currency');
  const country = overrideCountry ?? config.get('country');

  const locale = `${language}-${country}` as Locale;

  price = parseFloat((price || 0).toString());

  // Create formatter
  const formatter = getNumberFormatter(locale, {
    style: type || 'currency',
    currency: !disableCurrency ? currency : undefined,
    minimumFractionDigits: minAmountOfCents,
    ...rest,
  });

  // Price to float
  const priceFloat = parseFloat(price.toFixed(2));

  return customFormatter(formatter, priceFloat);
}

/**
 * Checks if formatted price starts with currency unit.
 * Input can be either price number or already formatted price.
 */
export function priceStartsWithCurrencyUnit(price: string | number, formatPrice: FormatPriceFn) {
  // Even if input is formatted formatting it again should not break anything.
  const formattedPrice = formatPrice(price);

  // Create a regex pattern to check if the string starts with a number
  const pattern = /^\d/;

  // Test if the string starts with a number
  return !pattern.test(formattedPrice);
}

/**
 * Converts string to correct price
 * @param priceString
 */
export function stringToPrice(priceString: string) {
  // Null or undefined not allowed
  if ([null, undefined].includes(priceString)) {
    return priceString;
  }

  // Clean input
  let formattedPrice = priceString?.toString()?.trim();

  // Number only
  formattedPrice = formattedPrice.replace(/[^.,\d]/gim, '').replace(/,/gim, '.');

  // If starts with dot, remove it
  if (formattedPrice.startsWith('.')) {
    formattedPrice = formattedPrice.slice(1);
  }

  // Only one dot allowed
  const [preDot, ...postDot] = formattedPrice.split('.');
  const postDotCleaned = postDot.join('').trim().substr(0, 2); // two numbers after dot

  // If after dot part exist or there are more than one dot in the end of string
  if (postDotCleaned || postDot.length > 1) {
    formattedPrice = [preDot, postDotCleaned].join('.');
  }

  return formattedPrice;
}

/**
 * Currency symbol that is needed for some requests
 */
export function getCurrencySymbol() {
  const currencySymbol = config.get('currencySymbol');

  return currencySymbol;
}

/**
 * Converts all commas to dots
 * @param formatter
 * @param price
 */
function customFormatter(formatter: Intl.NumberFormat, price: number) {
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/formatToParts
  const priceParts = formatter.formatToParts(price);

  return priceParts
    .map(({ type, value }) => {
      switch (type) {
        // Replace commas with dots.
        case 'decimal': {
          return '.';
        }
        case 'currency': {
          /*
           ** Change the displayed currency for Ksh.
           ** This format is used by official institutions like the Central Bank of Kenya, banks, and most formal publications.
           ** The lowercase "Ksh" is common in informal use, but "KSh" is the standard and correct form.
           */
          if (value === 'Ksh') {
            return 'KSh';
          }

          return value;
        }
        default: {
          return value;
        }
      }
    })
    .reduce((string, part) => string + part);
}
